// Copyright (c) Microsoft Corporation. All rights reserved.
// Licensed under the MIT License.
import { doAwaitResponse } from "@nevware21/ts-async";
import { arrIndexOf, isNumber, isString, mathRandom, objCreate, objDefineProps, objForEachKey, scheduleTimeout, strEndsWith, strLower, strStartsWith, strSubstring, utcNow } from "@nevware21/ts-utils";
import { _DYN_CANCEL, _DYN_CONFIG, _DYN_ENABLED, _DYN_ENDPOINT, _DYN_ENDPOINT_URL, _DYN_EXCEPTION, _DYN_FAILURE, _DYN_HOST, _DYN_INDEX_OF, _DYN_INSTRUMENTATION_KEY, _DYN_INTERVAL, _DYN_I_KEY, _DYN_LENGTH, _DYN_PENDING, _DYN_PUSH, _DYN_REJECTED, _DYN_REPLACE, _DYN_REQUEST_DURATION, _DYN_SET_DF, _DYN_SPLICE, _DYN_STATUS, _DYN_STRINGIFY, _DYN_SUCCESS, _DYN_THROTTLE, _DYN_TOTAL_REQUEST, _DYN_TRACK, _DYN_UNLOAD, _DYN_VALUE, _DYN_WATCH_INTERVAL } from "../__DynamicConstants";
import { onConfigChange } from "../config/DynamicConfig";
import { DEFAULT_BREEZE_PATH, DisabledPropertyName, SampleRate } from "../constants/Constants";
import { STR_EMPTY } from "../constants/InternalConstants";
import { _throwInternal, safeGetLogger } from "../diagnostics/DiagnosticLogger";
import { MetricDataType } from "../telemetry/ai/DataTypes";
import { getJSON, isFetchSupported, isXhrSupported } from "../utils/EnvUtils";
import { getResponseText, isFeatureEnabled, openXhr } from "../utils/HelperFuncs";
import { utlGetSessionStorage, utlSetSessionStorage } from "../utils/StorageHelperFuncs";
/** Default collection interval in seconds; override with `stats.shrtInt`. */
var STATS_COLLECTION_INTERVAL_SECONDS = 3600; // 1 hour
var STATS_LANGUAGE = "JavaScript";
var STATS_SAMPLING_PERCENTAGE = 100;
var STATS_TYPE = "Browser";
/** Ingestion path for future 1DS (OneCollector) SDK Stats; the AI SKU uses {@link DEFAULT_BREEZE_PATH}. */
export var STATS_SDK_ONECOLLECTOR_PATH = "/OneCollector/1.0";
/**
 * The default feature name used to gate the SDK Stats manager. SDK Stats is enabled by default
 * and can be opted-out via the featureOptIn configuration using this name.
 */
export var STATS_SDK_FEATURE = "sdkStats";
// Prefixes for EU data-boundary regions used by the Azure Monitor OpenTelemetry exporter
var STATS_EU_REGION_PATTERN = /^(?:[^:]+:\/\/)?(?:france|germany|northeurope|norway|sweden|switzerland|uk|westeurope)/i;
/**
 * Returns the SDK Stats config URL (`cfg/v1.json`) for the endpoint, derived from the configured
 * base url. For EU data-boundary endpoints an `eu-` prefix is inserted in front of
 * the host, e.g. `https://data.stats...` => `https://eu-data.stats...`.
 * @param endpoint - The customer breeze endpoint that the SDK Stats are being collected for.
 * @param cfgUrl - The configured (non-EU) SDK Stats config url, when not supplied null is returned.
 * @returns The config url to fetch, or null when no url has been configured.
 */
export function getStatsCfgUrl(endpoint, cfgUrl) {
    var result = null;
    if (cfgUrl) {
        result = cfgUrl;
        if (STATS_EU_REGION_PATTERN.test(endpoint)) {
            result = cfgUrl[_DYN_REPLACE /* @min:%2ereplace */](/^([^:]+:\/\/)?/, "$1eu-");
        }
    }
    return result;
}
/** Parse the SDK Stats config JSON (`{ ver, enabled, url }`); null if empty or unparseable. */
function _parseStatsCfg(response) {
    var result = null;
    var json = getJSON();
    if (response && json) {
        try {
            var cfg = json.parse(response);
            if (cfg) {
                result = {
                    // Fail-closed: only treat as enabled when explicitly true
                    enabled: cfg[_DYN_ENABLED /* @min:%2eenabled */] === true,
                    url: isString(cfg.url) ? cfg.url : null
                };
            }
        }
        catch (e) {
            // Unparseable -> no config available
        }
    }
    return result;
}
/** Default SDK Stats config fetch (fetch, else XHR); calls oncomplete with the parsed config or null. */
function _defaultStatsCfgFetch(cfgUrl, oncomplete) {
    function _complete(response) {
        try {
            oncomplete(response ? _parseStatsCfg(response) : null);
        }
        catch (e) {
            // Ignore callback errors
        }
    }
    try {
        if (isFetchSupported()) {
            var init = { method: "GET" };
            init[DisabledPropertyName] = true;
            doAwaitResponse(fetch(cfgUrl, init), function (result) {
                var response = result[_DYN_VALUE /* @min:%2evalue */];
                if (!result[_DYN_REJECTED /* @min:%2erejected */] && response && response.ok) {
                    doAwaitResponse(response.text(), function (res) {
                        _complete(res[_DYN_REJECTED /* @min:%2erejected */] ? null : res[_DYN_VALUE /* @min:%2evalue */]);
                    });
                }
                else {
                    _complete();
                }
            });
        }
        else if (isXhrSupported()) {
            // openXhr marks the request disabled so it isn't self-tracked
            var xhr_1 = openXhr("GET", cfgUrl, false, true, false, 10000);
            xhr_1.onreadystatechange = function () {
                if (xhr_1.readyState === 4) {
                    _complete(xhr_1[_DYN_STATUS /* @min:%2estatus */] >= 200 && xhr_1[_DYN_STATUS /* @min:%2estatus */] < 400 ? getResponseText(xhr_1) : null);
                }
            };
            xhr_1.onerror = function () {
                _complete();
            };
            xhr_1.ontimeout = function () {
                _complete();
            };
            xhr_1.send();
        }
        else {
            _complete();
        }
    }
    catch (e) {
        _complete();
    }
}
/** Build the ingestion endpoint from the config host: `https://<host>` + {@link DEFAULT_BREEZE_PATH}. */
function _buildStatsEndpoint(host) {
    var endpoint = null;
    if (host) {
        var base = strStartsWith(strLower(host), "http") ? host : "https://" + host;
        if (strEndsWith(base, "/")) {
            base = strSubstring(base, 0, base[_DYN_LENGTH /* @min:%2elength */] - 1);
        }
        endpoint = base + DEFAULT_BREEZE_PATH;
    }
    return endpoint;
}
/**
 * Creates a new IInternalSdkStatsNetwork instance with the specified host.
 * @param host - The host for the IInternalSdkStatsNetwork instance.
 * @returns A new IInternalSdkStatsNetwork instance.
 */
function _createInternalSdkStatsNetwork(host) {
    var _a;
    return _a = {
            host: host,
            totalRequest: 0,
            success: 0,
            // Untrusted keys must not reach Object.prototype.
            throttle: objCreate(null),
            failure: objCreate(null),
            retry: objCreate(null),
            exception: objCreate(null)
        },
        _a[_DYN_REQUEST_DURATION /* @min:requestDuration */] = 0,
        _a;
}
/** Validates persisted counts and restores them to a null-prototype object. */
function _reviveCounts(src) {
    var result = objCreate(null);
    objForEachKey(src, function (key, value) {
        value = +value;
        if (value > 0) {
            result[key] = value;
        }
    });
    return result;
}
/** Restores validated counters from session storage. */
function _reviveCounters(host, cnt) {
    var counter = _createInternalSdkStatsNetwork(host);
    if (cnt) {
        counter[_DYN_TOTAL_REQUEST /* @min:%2etotalRequest */] = +cnt[_DYN_TOTAL_REQUEST /* @min:%2etotalRequest */] || 0;
        counter[_DYN_SUCCESS /* @min:%2esuccess */] = +cnt[_DYN_SUCCESS /* @min:%2esuccess */] || 0;
        counter[_DYN_REQUEST_DURATION /* @min:%2erequestDuration */] = +cnt[_DYN_REQUEST_DURATION /* @min:%2erequestDuration */] || 0;
        counter[_DYN_THROTTLE /* @min:%2ethrottle */] = _reviveCounts(cnt[_DYN_THROTTLE /* @min:%2ethrottle */]);
        counter[_DYN_FAILURE /* @min:%2efailure */] = _reviveCounts(cnt[_DYN_FAILURE /* @min:%2efailure */]);
        counter.retry = _reviveCounts(cnt.retry);
        counter[_DYN_EXCEPTION /* @min:%2eexception */] = _reviveCounts(cnt[_DYN_EXCEPTION /* @min:%2eexception */]);
    }
    return counter;
}
function _loadStore(logger, key) {
    try {
        var json = getJSON();
        var raw = json && utlGetSessionStorage(logger, key);
        if (raw) {
            var parsed = json.parse(raw);
            if (parsed[0] >= 0) {
                return parsed;
            }
        }
    }
    catch (e) {
        // Start a new window.
    }
    return null;
}
function _saveStore(logger, key, store) {
    try {
        var json = getJSON();
        if (json) {
            utlSetSessionStorage(logger, key, json[_DYN_STRINGIFY /* @min:%2estringify */](store));
        }
    }
    catch (e) {
        // Persistence is best-effort; serialization can fail when built-in prototypes are modified.
    }
}
/**
 * Creates a new IInternalSdkStats instance with the specified manager callbacks and internalSdkStats state.
 * @param mgr - The manager callbacks to use for the IInternalSdkStats instance.
 * @param internalSdkStatsStats - The internalSdkStats state to use for the IInternalSdkStats instance.
 * @returns A new IInternalSdkStats instance.
 */
function _createInternalSdkStats(mgr, internalSdkStatsStats, logger) {
    // Isolate counters by customer key and endpoint.
    var _storeKey = (internalSdkStatsStats.cKey || STR_EMPTY) + ":" + internalSdkStatsStats[_DYN_ENDPOINT /* @min:%2eendpoint */];
    var _store = _loadStore(logger, _storeKey);
    // Start a new window only when the first value is recorded.
    var _windowStart = _store ? _store[0] : -1;
    var _networkCounter = _reviveCounters(internalSdkStatsStats[_DYN_ENDPOINT /* @min:%2eendpoint */], _store && _store[1]);
    var _timeoutHandle; // Handle to the timer for sending telemetry. This way, we would not send telemetry when system sleep.
    var _isEnabled = true; // Flag to check if internalSdkStats is enabled or not
    var _removeIntervalListener;
    function _startWindow() {
        if (_windowStart < 0) {
            _windowStart = utcNow();
        }
    }
    function _persist() {
        _saveStore(logger, _storeKey, [_windowStart, _networkCounter]);
    }
    /** Returns the remaining time in the current window. */
    function _remaining() {
        var interval = mgr[_DYN_INTERVAL /* @min:%2einterval */]();
        var elapsed = utcNow() - _windowStart;
        var remaining = interval;
        if (elapsed >= 0) {
            remaining = interval - elapsed;
        }
        return remaining > 0 ? remaining : 0;
    }
    function _setupTimer() {
        if (_isEnabled && _windowStart >= 0 && !_timeoutHandle) {
            _timeoutHandle = mgr.start(function () {
                _timeoutHandle = null;
                trackInternalSdkStats();
            }, _remaining());
        }
    }
    function trackInternalSdkStats() {
        if (_isEnabled) {
            var ready = mgr[_DYN_TRACK /* @min:%2etrack */](internalSdkStats);
            if (ready !== null) {
                if (ready) {
                    _trackSendRequestDuration();
                    _trackSendRequestsCount();
                }
                _networkCounter = _createInternalSdkStatsNetwork(_networkCounter[_DYN_HOST /* @min:%2ehost */]);
                // Persist the reset state; restart the window on the next value.
                _windowStart = -1;
                _persist();
            }
            else {
                // Keep the counters and retry during the next interval.
                _windowStart = utcNow();
                _persist();
                _setupTimer();
            }
        }
    }
    /**
     * This is a simple helper that checks if the currently reporting endpoint is the same as this instance was
     * created with. This is used to ensure that we only send internalSdkStats events to the endpoint that was used
     * when the instance was created. This is important as the endpoint can change during the lifetime of the
     * instance and we don't want to send internalSdkStats events to the wrong endpoint.
     * @param endpoint
     * @returns true if the endpoint is the same as the one used to create the instance, false otherwise
     */
    function _checkEndpoint(endpoint) {
        return _networkCounter[_DYN_HOST /* @min:%2ehost */] === endpoint;
    }
    function _inc(counter, key) {
        counter[key] = (counter[key] || 0) + 1;
    }
    /**
     * Attempt to send internalSdkStats events to the server. This is done by creating a new event and sending it to the core.
     * The event is created with the name and value passed in, and any additional properties are added to the event as well.
     * This will only send the event when
     * - the internalSdkStats is enabled
     * - the internalSdkStats key is set for the current endpoint
     * - the value is greater than 0
     * @param name - The name of the event to send
     * @param val - The value of the event to send
     * @param properties - Optional additional properties to add to the event
     */
    function _sendInternalSdkStatss(name, val, properties) {
        if (_isEnabled && val && val > 0) {
            // Add extra properties
            var baseProperties = {
                "rp": "unknown",
                "attach": "Manual",
                "cikey": internalSdkStatsStats.cKey,
                "os": STATS_TYPE,
                "language": STATS_LANGUAGE,
                "version": internalSdkStatsStats.sdkVer || "unknown",
                "endpoint": "breeze",
                "host": _networkCounter[_DYN_HOST /* @min:%2ehost */]
            };
            var combinedProps_1 = {};
            objForEachKey(properties, function (key, value) {
                combinedProps_1[key] = value;
            });
            objForEachKey(baseProperties, function (key, value) {
                combinedProps_1[key] = value;
            });
            var internalSdkStatsEvent = {
                name: name,
                baseData: {
                    name: name,
                    average: val,
                    properties: combinedProps_1
                },
                baseType: MetricDataType
            };
            // The destination iKey and (optional) SDK Stats ingestion endpoint are resolved and
            // stamped by the manager (see _track) based on the current (dynamic) configuration.
            mgr[_DYN_TRACK /* @min:%2etrack */](internalSdkStats, internalSdkStatsEvent);
        }
    }
    function _trackSendRequestDuration() {
        var totalRequest = _networkCounter[_DYN_TOTAL_REQUEST /* @min:%2etotalRequest */];
        if (totalRequest > 0) {
            _sendInternalSdkStatss("Request_Duration", _networkCounter[_DYN_REQUEST_DURATION /* @min:%2erequestDuration */] / totalRequest);
        }
    }
    function _sendCounts(counts, name, codeKey) {
        for (var code in counts) {
            var props = {};
            props[codeKey] = code;
            _sendInternalSdkStatss(name, counts[code], props);
        }
    }
    function _trackSendRequestsCount() {
        var currentCounter = _networkCounter;
        _sendInternalSdkStatss("Request_Success_Count", currentCounter[_DYN_SUCCESS /* @min:%2esuccess */]);
        _sendCounts(currentCounter[_DYN_FAILURE /* @min:%2efailure */], "Request_Failure_Count", "statusCode");
        _sendCounts(currentCounter.retry, "Retry_Count", "statusCode");
        _sendCounts(currentCounter[_DYN_EXCEPTION /* @min:%2eexception */], "Exception_Count", "exceptionType");
        _sendCounts(currentCounter[_DYN_THROTTLE /* @min:%2ethrottle */], "Throttle_Count", "statusCode");
    }
    function _setEnabled(isEnabled) {
        _isEnabled = isEnabled;
        if (!_isEnabled) {
            if (_timeoutHandle) {
                _timeoutHandle[_DYN_CANCEL /* @min:%2ecancel */]();
                _timeoutHandle = null;
            }
            if (_removeIntervalListener) {
                _removeIntervalListener();
                _removeIntervalListener = null;
            }
        }
    }
    _removeIntervalListener = mgr[_DYN_WATCH_INTERVAL /* @min:%2ewatchInterval */](function () {
        if (_timeoutHandle) {
            _timeoutHandle[_DYN_CANCEL /* @min:%2ecancel */]();
            _timeoutHandle = null;
        }
        _setupTimer();
    });
    // THE internalSdkStats instance being created and returned
    var internalSdkStats = {
        enabled: !!_isEnabled,
        endpoint: STR_EMPTY,
        count: function (status, payloadData, endpoint) {
            if (_isEnabled && _checkEndpoint(endpoint)) {
                var statsData = payloadData && payloadData["statsData"];
                var startTime = statsData && statsData["startTime"];
                if (startTime) {
                    _networkCounter[_DYN_TOTAL_REQUEST /* @min:%2etotalRequest */]++;
                    _networkCounter[_DYN_REQUEST_DURATION /* @min:%2erequestDuration */] += utcNow() - startTime;
                }
                var retryArray = [401, 403, 408, 429, 500, 502, 503, 504];
                var throttleArray = [402, 439];
                if (status >= 200 && status < 300) {
                    _networkCounter[_DYN_SUCCESS /* @min:%2esuccess */]++;
                }
                else if (retryArray[_DYN_INDEX_OF /* @min:%2eindexOf */](status) !== -1) {
                    _inc(_networkCounter.retry, status);
                }
                else if (throttleArray[_DYN_INDEX_OF /* @min:%2eindexOf */](status) !== -1) {
                    _inc(_networkCounter[_DYN_THROTTLE /* @min:%2ethrottle */], status);
                }
                else if (status !== 307 && status !== 308) {
                    _inc(_networkCounter[_DYN_FAILURE /* @min:%2efailure */], status);
                }
                // Persist in case the page unloads before the interval ends.
                _startWindow();
                _persist();
                _setupTimer();
            }
        },
        countException: function (endpoint, exceptionType) {
            if (_isEnabled && _checkEndpoint(endpoint)) {
                _inc(_networkCounter[_DYN_EXCEPTION /* @min:%2eexception */], exceptionType);
                _startWindow();
                _persist();
                _setupTimer();
            }
        }
    };
    // Make the properties readonly / reactive to changes
    return objDefineProps(internalSdkStats, {
        enabled: { g: function () { return _isEnabled; }, s: _setEnabled },
        endpoint: { g: function () { return _networkCounter[_DYN_HOST /* @min:%2ehost */]; } }
    });
}
export function createStatsMgr() {
    var _isMgrEnabled = false; // Flag to check if internalSdkStats is enabled or not
    var _core; // The customer core observed for configuration and endpoint changes
    var _createStatsCore;
    var _featureName;
    var _statsCore;
    var _useFeature;
    var _sampleRate = STATS_SAMPLING_PERCENTAGE;
    var _shortInterval = STATS_COLLECTION_INTERVAL_SECONDS * 1000;
    var _statsCfgFetchFn;
    var _statsIKey;
    // The configured SDK Stats config url (cfg/v1.json), sourced from the dynamic config. When it is
    // not supplied (no CDN / host configuration) SDK Stats are not collected or sent.
    var _statsCfgUrl;
    // Resolved remote config cached per cfg URL (EU / non-EU); tracks in-flight fetch and last result.
    // Null-prototype so the config supplied url can never be used to pollute Object.prototype.
    var _cfgCache = objCreate(null);
    var _endpointCfgCache = objCreate(null);
    var _intervalListeners = [];
    function _unloadStatsCore() {
        var statsCore = _statsCore;
        _statsCore = null;
        statsCore && statsCore[_DYN_UNLOAD /* @min:%2eunload */](false);
    }
    function _getStatsCore(endpoint) {
        var _a;
        if (_statsCore && _statsCore.config[_DYN_ENDPOINT_URL /* @min:%2eendpointUrl */] !== endpoint) {
            _unloadStatsCore();
        }
        if (!_statsCore) {
            _statsCore = _createStatsCore((_a = {},
                _a[_DYN_INSTRUMENTATION_KEY /* @min:instrumentationKey */] = _statsIKey,
                _a.endpointUrl = endpoint,
                _a));
        }
        return _statsCore;
    }
    // Lazily initialize the manager and start listening for configuration changes
    // This is also required to handle "unloading" and then re-initializing again
    function _init(core, createStatsCore, featureName, useFeature) {
        if (_core) {
            // If the core is already set, then just return with an empty unload hook
            _throwInternal(safeGetLogger(core), 2 /* eLoggingSeverity.WARNING */, 113 /* _eInternalMessageId.InternalSdkStatsManagerException */, "InternalSdkStats manager is already initialized");
            return null;
        }
        _core = core;
        _createStatsCore = createStatsCore;
        _featureName = featureName || STATS_SDK_FEATURE;
        _useFeature = useFeature;
        // Start listening for configuration changes from the single global config, within a config
        // change handler. This supports the scenario where the config is changed after the manager
        // has been created (including CDN / dynamic config updates).
        var configHook = onConfigChange(core[_DYN_CONFIG /* @min:%2econfig */], function (details) {
            var previousInterval = _shortInterval;
            var previousCfgUrl = _statsCfgUrl;
            var previousIKey = _statsIKey;
            // Re-evaluate the feature flag on every config change (enabled by default, opt-out via featureOptIn)
            _isMgrEnabled = false;
            _statsCfgFetchFn = null;
            _statsIKey = null;
            _statsCfgUrl = null;
            _sampleRate = STATS_SAMPLING_PERCENTAGE;
            if (isFeatureEnabled(_featureName, details.cfg, true) === true) {
                // Seed the SDK Stats defaults into the single global config so they remain dynamic and
                // can be overridden via the CDN / dynamic config or by the SKU.
                details[_DYN_SET_DF /* @min:%2esetDf */](details.cfg, _sdkStatsDefaults);
                // Read the nested stats config directly (registers the dynamic dependency on the
                // stats object) and copy the individual values into local (minifiable) variables
                // instead of holding the config object and repeatedly reading its properties.
                var statsCfg = details.cfg.stats;
                if (statsCfg) {
                    details[_DYN_SET_DF /* @min:%2esetDf */](statsCfg, _sdkStatsConfigDefaults);
                    // Make the override fetch fn a dynamic property before snapshotting it so a later
                    // merged (CDN / updateCfg) change to it re-runs this handler and refreshes the local.
                    _statsCfgFetchFn = details.set(statsCfg, "overrideCfgFn", statsCfg.overrideCfgFn);
                    _statsIKey = details.set(statsCfg, "iKey", statsCfg[_DYN_I_KEY /* @min:%2eiKey */]);
                    // Same for the config url, it is normally delivered by the CDN configuration after
                    // initialization has completed, so this handler must re-run when it arrives.
                    _statsCfgUrl = details.set(statsCfg, "cfgUrl", statsCfg.cfgUrl);
                    var sampleRate = details.set(statsCfg, "samplingPercentage", statsCfg.samplingPercentage);
                    if (isNumber(sampleRate) && sampleRate >= 0 && sampleRate <= 100) {
                        _sampleRate = sampleRate;
                    }
                    _isMgrEnabled = true;
                    _shortInterval = STATS_COLLECTION_INTERVAL_SECONDS * 1000; // Reset to the default in-case the config is removed / changed
                    if (isNumber(statsCfg.shrtInt) && statsCfg.shrtInt > 0) {
                        _shortInterval = statsCfg.shrtInt * 1000; // Convert to milliseconds
                    }
                }
            }
            if (!_isMgrEnabled || _statsCfgUrl !== previousCfgUrl || _statsIKey !== previousIKey) {
                _unloadStatsCore();
            }
            if (_statsCfgUrl !== previousCfgUrl) {
                _endpointCfgCache = objCreate(null);
            }
            if (_shortInterval !== previousInterval) {
                for (var lp = 0; lp < _intervalListeners[_DYN_LENGTH /* @min:%2elength */]; lp++) {
                    _intervalListeners[lp]();
                }
            }
        });
        return {
            rm: function () {
                configHook.rm();
                _unloadStatsCore();
                _createStatsCore = null;
                _featureName = null;
                _useFeature = null;
                _core = null;
            }
        };
    }
    /**
     * Resolve the remote SDK Stats config for the endpoint, starting a fetch on first use. Returns
     * null until resolved (or on failure, or when no config url has been configured) so the caller
     * skips sending.
     */
    function _resolveStatsCfg(endpoint) {
        var cfgUrl = _endpointCfgCache[endpoint];
        if (!cfgUrl) {
            cfgUrl = getStatsCfgUrl(endpoint, _statsCfgUrl);
            if (cfgUrl) {
                _endpointCfgCache[endpoint] = cfgUrl;
            }
        }
        if (!cfgUrl) {
            // No configured SDK Stats config url -> nothing to resolve and nothing is sent
            return null;
        }
        var entry = _cfgCache[cfgUrl];
        if (!entry) {
            entry = _cfgCache[cfgUrl] = { pending: false, result: null };
        }
        if (!entry.result && !entry[_DYN_PENDING /* @min:%2epending */]) {
            entry[_DYN_PENDING /* @min:%2epending */] = true;
            var fetchFn = _statsCfgFetchFn || _defaultStatsCfgFetch;
            try {
                fetchFn(cfgUrl, function (result) {
                    entry[_DYN_PENDING /* @min:%2epending */] = false;
                    // null on failure so a later interval retries
                    entry.result = result;
                });
            }
            catch (e) {
                // Reset so a later interval retries
                entry[_DYN_PENDING /* @min:%2epending */] = false;
            }
        }
        return entry.result;
    }
    function _track(internalSdkStats, internalSdkStatsEvent) {
        if (_isMgrEnabled) {
            if (!_statsIKey) {
                return null;
            }
            // The remote cfg file is the sole authority for whether collection is enabled and where
            // to send the events. Re-resolved here (rather than cached on the instance) to support the
            // endpoint changing after the instance was created.
            var cfgResult = _resolveStatsCfg(internalSdkStats[_DYN_ENDPOINT /* @min:%2eendpoint */]);
            if (!cfgResult) {
                return null;
            }
            if (!cfgResult[_DYN_ENABLED /* @min:%2eenabled */]) {
                return false;
            }
            var sampleRate_1 = _sampleRate;
            if (!internalSdkStatsEvent && sampleRate_1 > 0) {
                // Validate the pipeline before the counters are reset. At 0% no pipeline is required.
                var statsCore = _getStatsCore(_buildStatsEndpoint(cfgResult.url));
                if (!statsCore) {
                    return null;
                }
            }
            else if (internalSdkStatsEvent &&
                (sampleRate_1 >= 100 || (sampleRate_1 > 0 && mathRandom() * 100 < sampleRate_1))) {
                var trackStats = function () {
                    var currentCfg = _resolveStatsCfg(internalSdkStats[_DYN_ENDPOINT /* @min:%2eendpoint */]);
                    if (currentCfg && currentCfg[_DYN_ENABLED /* @min:%2eenabled */]) {
                        var statsCore = _getStatsCore(_buildStatsEndpoint(currentCfg.url));
                        if (statsCore) {
                            internalSdkStatsEvent[SampleRate] = sampleRate_1;
                            statsCore[_DYN_TRACK /* @min:%2etrack */](internalSdkStatsEvent);
                        }
                    }
                };
                if (_useFeature) {
                    _useFeature(_featureName, trackStats, true);
                }
                else {
                    trackStats();
                }
            }
            return true;
        }
        return false;
    }
    function _watchInterval(cb) {
        _intervalListeners[_DYN_PUSH /* @min:%2epush */](cb);
        return function () {
            var idx = arrIndexOf(_intervalListeners, cb);
            if (idx !== -1) {
                _intervalListeners[_DYN_SPLICE /* @min:%2esplice */](idx, 1);
            }
        };
    }
    function _createInstance(state) {
        var _a;
        var instance = null;
        if (_isMgrEnabled) {
            // Prefetch the remote config so it's ready by the first interval
            if (state && state[_DYN_ENDPOINT /* @min:%2eendpoint */]) {
                _resolveStatsCfg(state[_DYN_ENDPOINT /* @min:%2eendpoint */]);
            }
            var callbacks = (_a = {
                    start: function (cb, delay) {
                        return scheduleTimeout(cb, delay);
                    },
                    interval: function () { return _shortInterval; }
                },
                _a[_DYN_WATCH_INTERVAL /* @min:watchInterval */] = _watchInterval,
                _a.track = _track,
                _a);
            instance = _createInternalSdkStats(callbacks, state, safeGetLogger(_core));
        }
        return instance;
    }
    var theMgr = {
        enabled: false,
        newInst: _createInstance,
        init: _init
    };
    return objDefineProps(theMgr, {
        "enabled": { g: function () { return _isMgrEnabled; } }
    });
}
/**
 * The default {@link IInternalSdkStatsConfig} values for SDK Stats collection. These are seeded into the
 * single global config (via {@link IWatchDetails.setDf}) by the manager so they remain dynamic and
 * can be overridden at runtime via the CDN / dynamic config or by the SKU (AISKU / 1DS). No config
 * url is defaulted, the `stats.cfgUrl` value is expected to be delivered by the CDN / dynamic
 * configuration, and until it is, no SDK Stats are collected or sent. The events are routed to the
 * distro-owned SDK Stats ingestion endpoint, whose host (and whether collection is enabled) is read
 * at runtime from the SDK Stats configuration identified by that url. SDK Stats can also be
 * opted-out using the `featureOptIn` configuration with the {@link STATS_SDK_FEATURE} name.
 */
var _sdkStatsDefaults = {
    // Seeding a stats object allows the manager to apply its nested defaults; the config url, destination and
    // enabled state are all resolved from the dynamic / remote SDK Stats configuration. A plain
    // object (rather than cfgDfMerge) is used so setDf seeds and makes the stats property dynamic
    // without marking it as a reference (avoiding the in-place reference side effect).
    stats: {}
};
var _sdkStatsConfigDefaults = {
    samplingPercentage: STATS_SAMPLING_PERCENTAGE
};
//# sourceMappingURL=InternalSdkStats.js.map